import MetaWrapper from '@/features/meta/components/MetaWrapper';
import { Box, Flex, Skeleton } from '@mantine/core';

import { useGetChoices } from '@/features/choices/services/choices';
import type { ResourcePaths } from '@/features/choices/types';

import { isNotNull } from '@/utils';

import { Cardinalities, FiltersTypes } from '@/types/meta';
import type { Schema } from '@/types/mercury-data-types/unifiedapi';
import type { MetaStore } from '@/hooks/useMetaStore';

const RAW_INTEL_CHOICES_PATH = '/issue/raw_intel/choices/{attr}' as ResourcePaths;

const getChoicesFromUnified = (choice: Schema<'ChoiceResponse'> & { value: string | number }) => ({
  label: choice.label ?? String(choice.value),
  description: choice.label ?? String(choice.value),
  value: choice.value,
  parent_id: choice.parent_id,
});

const toMetaChoices = (data: unknown) =>
  (Array.isArray(data) ? data : [])
    .filter(
      (choice): choice is Schema<'ChoiceResponse'> & { value: string | number } =>
        choice?.value != null
    )
    .map(getChoicesFromUnified);

const SearchFilters = ({ metaStore }: { metaStore: MetaStore }) => {
  const industries = useGetChoices(RAW_INTEL_CHOICES_PATH, 'industries', true);
  const locations = useGetChoices(RAW_INTEL_CHOICES_PATH, 'locations', true);

  if (industries.isPending || locations.isPending) {
    return (
      <Flex gap={15} mt={25}>
        <Skeleton height={35} radius="4px" />
        <Skeleton height={35} radius="4px" />
        <Skeleton height={35} radius="4px" />
      </Flex>
    );
  }

  const metaData = {
    meta: [
      {
        name: 'timeframe',
        label: 'Timeframe',
        description: '',
        type: FiltersTypes.date,
        cardinality: Cardinalities.range,
      },
      !industries.isError
        ? {
            name: 'industries.id',
            type: FiltersTypes.string,
            cardinality: Cardinalities.multiple,
            description: 'Industries',
            label: 'Industries',
            choices: toMetaChoices(industries.data),
          }
        : null,
      !locations.isError
        ? {
            name: 'locations.id',
            type: FiltersTypes.string,
            cardinality: Cardinalities.multiple,
            description: 'Locations',
            label: 'Locations',
            choices: toMetaChoices(locations.data),
          }
        : null,
    ].filter(isNotNull),
    defaultValues: null,
  };

  return (
    <Box mt={25}>
      <MetaWrapper metaData={metaData} metaStore={metaStore} hideSearch hideClear />
    </Box>
  );
};

export default SearchFilters;
